/**
 * Formatadores pt-BR — moeda, número, percentual.
 * fmtBRL aceita string|number (decimals da API vêm como string no JSON
 * do Pydantic) e flag de abreviação pra KPIs compactos — delega pro
 * fmtBRLcompact (sufixo único da casa: R$ 14,8 mi / R$ 5 mil).
 */

export function fmtBRL(
  value: string | number,
  opts?: { abbreviate?: boolean; decimals?: 0 | 2 },
): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(n)) return "—";
  // UX 03/07 item 7: UM formatador abreviado só (sufixo da casa "mi/mil",
  // #251) — o ramo M/K antigo divergia do fmtBRLcompact e a UI mostrava
  // "R$ 7,2M" e "R$ 7,3 mi" lado a lado.
  if (opts?.abbreviate) return fmtBRLcompact(n);
  const decimals = opts?.decimals ?? 2;
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** R$ compacto pt-BR SEM casas de centavo (#251): ≥1mi → "R$ 7,3 mi" (1 casa),
 * ≥1mil → "R$ 220 mil" (inteiro), abaixo → "R$ 543" (inteiro). Math.round em
 * tudo — nada de decimais de centavo vazando pra tela. null/NaN → "—". */
export function fmtBRLcompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  // >= 999.500 promove pro ramo "mi": senão o arredondamento do ramo "mil"
  // renderizava "R$ 1.000 mil" (review 03/07).
  if (abs >= 999_500) {
    return `${sign}R$ ${(abs / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mi`;
  }
  if (abs >= 1_000) {
    return `${sign}R$ ${Math.round(abs / 1_000).toLocaleString("pt-BR")} mil`;
  }
  return `${sign}R$ ${Math.round(abs).toLocaleString("pt-BR")}`;
}

/** Compacto COM sinal explícito (#253, casar o alvo): "+R$ 7,3 mi" / "−R$ 1,6 mi"
 * (− = minus unicode). 0 → "R$ 0" (sem sinal). null/NaN → "—". */
export function fmtBRLcompactSigned(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  if (value === 0) return fmtBRLcompact(0);
  const sign = value > 0 ? "+" : "−";
  return `${sign}${fmtBRLcompact(Math.abs(value))}`;
}

/** Valor COMPLETO inteiro COM sinal (#255 rev2): "−R$ 1.628.682" / "+R$ 3.731.989"
 * (− = minus unicode, milhar pt-BR, Math.round — sem centavos). 0 → "R$ 0".
 * null/NaN → "—". Usado nas LINHAS Δ (valor cheio, NÃO abreviado). */
export function fmtBRLintSigned(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const mag = `R$ ${Math.round(Math.abs(value)).toLocaleString("pt-BR")}`;
  if (value === 0) return mag;
  return `${value > 0 ? "+" : "−"}${mag}`;
}

export function fmtNum(value: number, decimals = 2): string {
  if (Number.isNaN(value)) return "—";
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmtPct(value: number, decimals = 2): string {
  return fmtNum(value, decimals) + "%";
}

/** MWh em pt-BR sem decimais — "1.440 MWh" / "-720 MWh" / "0 MWh" (Task #80).
 * Aceita string (API) ou number; retorna "—" pra null/NaN. */
export function fmtMWh(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return (
    n.toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + " MWh"
  );
}

/** MWm em pt-BR com 2 decimais — "2,00 MWm" / "-1,00 MWm" (Task #80). */
export function fmtMWm(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return (
    n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " MWm"
  );
}
